/**
 * `/audits` (13-32, give-first): Oversight's internal audit list (the route guard and the "More" entry are
 * Oversight-only, the D-13-08-2 pattern).
 *
 * - **One paged read** of `Internal Audits` (`useAudits`, `$select` inside `$expand`), plus the practice audits this
 *   browser holds in practice mode. A failed read says so and keeps what it has; it never shows an empty list.
 * - **Four tabs** (`auditBucket`): Scheduled, Overdue (`DueOn` before Melbourne today and not Completed or Approved;
 *   amber, D-13-15-4), Awaiting approval (`Completed`), Approved. Each with its count.
 * - **Rows:** `AUD-<Id>` (a link to `/record/AUD-<Id>`), the audit name, business unit, kind, due `D MMM YYYY`
 *   (D-13-L-10), the auditor; "From Centro" on a carried row; when completed and when approved, on those tabs.
 *   Sorted as FIELDSET §6.6's views: due first, then the oldest waiting, then the newest approval.
 * - **"Schedule an audit"** opens the schedule form (`AuditScheduleForm`), saved through the Writer.
 */

import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PageContainer } from '../../components/layout';
import { Button, Spinner } from '../../components/ui';
import { AuditScheduleForm } from '../../components/record/panels/AuditPanel';
import type { AuditSaved } from '../../components/record/panels/AuditPanel';
import { useActor } from '../../hooks/useActor';
import { useAudits } from '../../hooks/useAudits';
import type { AuditsSource } from '../../hooks/useAudits';
import { useDisplayNames } from '../../hooks/useDisplayNames';
import { isCarried } from '../../config/fieldset/internalAudits';
import { auditBucket } from '../../lib/auditVerbs';
import type { AuditBucket, AuditRow } from '../../lib/auditVerbs';
import { formatDay, melbourneDayOf } from '../../lib/fieldValues';
import cardStyles from '../../components/record/record.module.css';
import styles from './AuditsPage.module.css';

const TABS: ReadonlyArray<{ id: AuditBucket; label: string; empty: string }> = [
  { id: 'scheduled', label: 'Scheduled', empty: 'Nothing is scheduled and not yet due.' },
  { id: 'overdue', label: 'Overdue', empty: 'No audit is past its due day.' },
  { id: 'awaiting-approval', label: 'Awaiting approval', empty: 'No completed audit is waiting for approval.' },
  { id: 'approved', label: 'Approved', empty: 'No audit has been approved yet.' },
];

const dayOf = (row: AuditRow, column: string): string => {
  const v = row.fields[column];
  return (typeof v === 'string' ? melbourneDayOf(v) : null) ?? '';
};

/** FIELDSET §6.6's orders: Scheduled by `DueOn` ascending; Approved by `ApprovedOn` descending. */
function sortRows(bucket: AuditBucket, rows: AuditRow[]): AuditRow[] {
  const by = (column: string, dir: 1 | -1) => (a: AuditRow, b: AuditRow) =>
    dir * dayOf(a, column).localeCompare(dayOf(b, column)) || a.ref.localeCompare(b.ref, 'en', { numeric: true });
  if (bucket === 'approved') return [...rows].sort(by('ApprovedOn', -1));
  if (bucket === 'awaiting-approval') return [...rows].sort(by('CompletedOn', 1));
  return [...rows].sort(by('DueOn', 1));
}

const text = (v: unknown) => (typeof v === 'string' && v.trim() !== '' ? v : null);

export interface AuditsPageProps {
  /** The preview injects synthetic rows; the app reads the live list. */
  source?: AuditsSource;
  now?: () => Date;
}

export default function AuditsPage({ source, now = () => new Date() }: AuditsPageProps) {
  const { actor } = useActor();
  const audits = useAudits(source);
  const [params, setParams] = useSearchParams();
  const asked = params.get('tab');
  const [scheduling, setScheduling] = useState(false);
  const [scheduled, setScheduled] = useState<AuditSaved | null>(null);

  const at = useMemo(() => (audits.readAt ? new Date(audits.readAt) : now()), [audits.readAt, now]);
  const buckets = useMemo(() => {
    const out: Record<AuditBucket, AuditRow[]> = { scheduled: [], overdue: [], 'awaiting-approval': [], approved: [] };
    for (const r of audits.rows) out[auditBucket(r, at)].push(r);
    for (const k of Object.keys(out) as AuditBucket[]) out[k] = sortRows(k, out[k]);
    return out;
  }, [audits.rows, at]);
  const tab: AuditBucket = TABS.some((t) => t.id === asked) ? (asked as AuditBucket) : 'scheduled';
  const rows = buckets[tab];
  const names = useDisplayNames(
    audits.rows.map((r) => text(r.fields.AuditorUPN) ?? '').filter(Boolean),
    actor,
  );

  const pick = (id: AuditBucket) => {
    const next = new URLSearchParams(params);
    next.set('tab', id);
    setParams(next, { replace: true });
  };

  return (
    <PageContainer>
      <header className={styles.head}>
        <p className={styles.kicker}>Oversight · Internal audits</p>
        <h1 className={styles.title}>Audits</h1>
        <p className={styles.lede}>
          Schedule internal audits and system reviews, record each one&apos;s completion, and approve it.
        </p>
        <div className={styles.headActions}>
          {!scheduling && (
            <Button
              type="button"
              onClick={() => {
                setScheduling(true);
                setScheduled(null);
              }}
            >
              Schedule an audit
            </Button>
          )}
          {audits.refreshing && <span className={styles.muted}>Refreshing…</span>}
        </div>
        {scheduled && (
          <p className={styles.savedNote} role="status">
            <Link to={`/record/${scheduled.ref}`}>{scheduled.ref}</Link> is scheduled
            {scheduled.mode === 'practice' ? ' in practice mode. Nothing was written to SharePoint.' : '.'}
            {scheduled.auditPending > 0 ? ` ${scheduled.auditPending} audit rows will be posted.` : ''}
          </p>
        )}
      </header>

      {scheduling && actor && (
        <section className={`${cardStyles.panel} ${styles.schedule}`} aria-labelledby="schedule-title">
          <div className={cardStyles.panelHead}>
            <h2 id="schedule-title" className={cardStyles.panelTitle}>
              Schedule an audit
            </h2>
            <Button type="button" variant="ghost" size="sm" onClick={() => setScheduling(false)}>
              Close
            </Button>
          </div>
          <AuditScheduleForm
            actor={actor}
            now={now}
            onSaved={(s) => {
              setScheduled(s);
              setScheduling(false);
              audits.reload();
            }}
          />
        </section>
      )}

      <div className={styles.tabs} role="tablist" aria-label="Audits by stage">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={t.id === tab}
            className={[styles.tab, t.id === tab ? styles.tabActive : '', t.id === 'overdue' && buckets.overdue.length > 0 ? styles.tabLate : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => pick(t.id)}
          >
            {t.label} <span className={styles.count}>{buckets[t.id].length}</span>
          </button>
        ))}
      </div>

      {audits.status === 'error' && (
        <div className={styles.failure} role="alert">
          <p>Couldn&apos;t read the audits from the Quality &amp; Safety site{audits.error ? `: ${audits.error}` : '.'}</p>
          <Button type="button" variant="secondary" size="sm" onClick={audits.reload}>
            Try again
          </Button>
        </div>
      )}

      {audits.status === 'loading' ? (
        <div className={styles.loading} role="status" aria-live="polite">
          <Spinner size="lg" />
          <span>Reading the audits…</span>
        </div>
      ) : rows.length === 0 ? (
        <p className={styles.empty}>{TABS.find((t) => t.id === tab)?.empty}</p>
      ) : (
        <ul className={styles.list} aria-label={TABS.find((t) => t.id === tab)?.label}>
          {rows.map((r) => {
            const f = r.fields;
            const auditor = text(f.AuditorUPN);
            const due = text(f.DueOn);
            return (
              <li key={r.ref} className={`${styles.row} ${tab === 'overdue' ? styles.rowLate : ''}`}>
                <Link to={`/record/${r.ref}`} className={styles.ref}>
                  {r.ref}
                </Link>
                <div className={styles.what}>
                  <Link to={`/record/${r.ref}`} className={styles.name}>
                    {text(f.Title) ?? r.ref}
                  </Link>
                  <span className={styles.meta}>
                    {[text(f.BusinessUnit), text(f.AuditKind) ?? 'Kind not recorded'].filter(Boolean).join(' · ')}
                    {isCarried(f) && <span className={styles.centro}>From Centro</span>}
                    {r.ref.includes('-P') && <span className={styles.centro}>Practice</span>}
                  </span>
                </div>
                <div className={styles.when}>
                  <span className={tab === 'overdue' ? styles.late : undefined}>
                    {due ? `Due ${formatDay(due)}` : 'No due day'}
                    {tab === 'overdue' ? ' · overdue' : ''}
                  </span>
                  {tab === 'awaiting-approval' && text(f.CompletedOn) && <span className={styles.muted}>Completed {formatDay(String(f.CompletedOn))}</span>}
                  {tab === 'approved' && (
                    <span className={styles.muted}>
                      {text(f.ApprovedOn) ? `Approved ${formatDay(String(f.ApprovedOn))}` : 'Approved'}
                      {typeof f.ScorePct === 'number' ? ` · ${f.ScorePct}%` : ''}
                    </span>
                  )}
                </div>
                <div className={styles.who}>{auditor ? (names.get(auditor.toLowerCase()) ?? auditor) : 'No auditor recorded'}</div>
              </li>
            );
          })}
        </ul>
      )}
    </PageContainer>
  );
}
